import Api from '@/api/helpers';
import { IComboItem } from '@/components/shared/ComboBox';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ILocationDto, SaveUserPreferencesRequest } from '../api/client';
import { IColumn, columns } from '../components/message-center/GridColumns';
import { TrashBinColumns } from '../components/trash-bin/TrashBinGridColumns';
import { IRequestState, instanceOfIErrorDetails } from './interfaces';
import { customToast } from '@/components/shared/Toasts';

export enum Theme {
  default,
  dark,
}

export interface IPreferencesState extends IRequestState {
  includeExpiredMessages: boolean;
  activeOnDateFilter: Date | undefined | null;
  theme: Theme;
  gridDataState: State;
  trashBinGridDataState: State;
  locationFilters: ILocationDto[];
  docTypePk: number | undefined;
  visibleColumns: IColumn[];
  trashBinVisibleColumns: IColumn[];
  testItems: IComboItem[];
}

const initialState: IPreferencesState = {
  includeExpiredMessages: true,
  activeOnDateFilter: null,
  theme: Theme.default,
  gridDataState: { skip: 0, take: 100 },
  trashBinGridDataState: { skip: 0, take: 100 },
  locationFilters: [],
  docTypePk: undefined,
  visibleColumns: columns,
  trashBinVisibleColumns: TrashBinColumns,
  isRequestActive: false,
  errorDetails: { statusCode: null, userMessage: null },
  testItems: [{ name: '' }, { name: 'test1' }, { name: 'test2' }],
};

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    receivePersistedState: (_, action: PayloadAction<IPreferencesState>) => {
      action.payload.gridDataState.filter?.filters.filter((filt) => {
        if ('filters' in filt) {
          const f = filt.filters[0] as FilterDescriptor;
          return f.field !== 'location.name';
        } else {
          return true;
        }
      });

      if (action.payload.gridDataState.filter?.filters)
        action.payload.gridDataState.filter.filters =
          action.payload.gridDataState.filter.filters.map((filt) => {
            if ('filters' in filt) {
              const f = filt.filters[0] as FilterDescriptor;
              const f2 = filt.filters[1] ? (filt.filters[1] as FilterDescriptor) : undefined;

              if (f.field?.toString().toLowerCase().includes('date'))
                if (f2) {
                  return {
                    ...filt,
                    filters: [
                      { ...f, value: f.value ? new Date(f.value) : null },
                      { ...f2, value: f2.value ? new Date(f2?.value) : null },
                    ],
                  };
                } else {
                  return {
                    ...filt,
                    filters: [{ ...f, value: f.value ? new Date(f.value) : null }],
                  };
                }
              else return filt;
            } else {
              return filt;
            }
          }) ?? [];
      return action.payload;
    },
    reset: () => initialState,
    clearLocationFiltersFromGridDataState: (state) => {
      if (state.gridDataState.filter) {
        let filters: CompositeFilterDescriptor[] = state.gridDataState.filter?.filters as CompositeFilterDescriptor[];
        if (!state.trashBinGridDataState.filter)
          state.trashBinGridDataState.filter = { filters: [] as CompositeFilterDescriptor[], logic: 'and' };
        let trashBinFilters: CompositeFilterDescriptor[] = state.trashBinGridDataState.filter
          ?.filters as CompositeFilterDescriptor[];
        filters = filters.filter((filt) => {
          if ('filters' in filt) {
            const f = filt.filters[0] as FilterDescriptor;
            return f.field !== 'location.name';
          } else {
            return true;
          }
        });
        trashBinFilters = trashBinFilters.filter((filt) => {
          if ('filters' in filt) {
            const f = filt.filters[0] as FilterDescriptor;
            return f.field !== 'location.name';
          } else {
            return true;
          }
        });
        state.gridDataState = { ...state.gridDataState, filter: { filters: filters, logic: 'and' } };
        state.trashBinGridDataState = {
          ...state.trashBinGridDataState,
          filter: { filters: trashBinFilters, logic: 'and' },
        };
      }
    },
    adjustColumnSize: (state, action: PayloadAction<{ grid: 'message' | 'trash'; columns: IColumn[] }>) => {
      if (action.payload.grid === 'message') state.visibleColumns = [...action.payload.columns];
      else if (action.payload.grid === 'trash') state.trashBinVisibleColumns = [...action.payload.columns];
    },
    adjustColumnOrder: (state, action: PayloadAction<{ grid: 'message' | 'trash'; columns: IColumn[] }>) => {
      if (action.payload.grid === 'message') state.visibleColumns = action.payload.columns;
      else if (action.payload.grid === 'trash') state.trashBinVisibleColumns = action.payload.columns;
    },
    setIncludeExpiredMessages: (state, action: PayloadAction<boolean>) => {
      state.includeExpiredMessages = action.payload;
    },
    setFilterMessagesByActiveDate: (state, action: PayloadAction<Date | undefined | null>) => {
      state.activeOnDateFilter = action.payload;
    },
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
    },
    setGridDataState: (state, action: PayloadAction<State>) => {
      state.gridDataState = {
        filter: action.payload.filter,
        group: action.payload.group,
        sort: action.payload.sort,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    },
    setTrashBinGridState: (state, action: PayloadAction<State>) => {
      state.trashBinGridDataState = {
        filter: action.payload.filter,
        group: action.payload.group,
        sort: action.payload.sort,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    },
    addLocationFilter: (state, action: PayloadAction<{ location: ILocationDto; docTypePk: number | undefined }>) => {
      if (state.docTypePk !== action.payload.docTypePk) state.locationFilters = [];
      state.locationFilters.push(action.payload.location);
      state.docTypePk = action.payload.docTypePk;
    },
    setLocationFilter: (state, action: PayloadAction<{ locations: ILocationDto[]; docTypePk: number | undefined }>) => {
      state.locationFilters = action.payload.locations;
      state.docTypePk = action.payload.docTypePk;
    },
    removeLocationFilter: (state, action: PayloadAction<ILocationDto>) => {
      state.locationFilters = state.locationFilters.filter((location) => location.name !== action.payload.name);
    },
    clearLocationFilters: (state) => {
      state.locationFilters = [];
    },
    setVisibleColumns: (state, action: PayloadAction<IColumn[]>) => {
      state.visibleColumns = action.payload;
    },
    resetVisibleColumns: (state) => {
      state.visibleColumns = initialState.visibleColumns;
    },
    setTrashBinVisibleColumns: (state, action: PayloadAction<IColumn[]>) => {
      state.trashBinVisibleColumns = action.payload;
    },
    resetTrashBinVisibleColumns: (state) => {
      state.trashBinVisibleColumns = initialState.trashBinVisibleColumns;
    },
  },
});

export const saveUserPreferences = createAsyncThunk('preferences/saveUserPreferences', async (_, thunkApi) => {
  const userClient = Api.getUserClient(thunkApi.dispatch);
  userClient.saveUserPreferences(
    new SaveUserPreferencesRequest({ preferencesJson: JSON.stringify((thunkApi.getState() as any).preferences) }),
  );
  return;
});

export const deleteUserPreferences = createAsyncThunk('/user/deleteUserPreferences', async (_, thunkApi) => {
  try {
    const userClient = Api.getUserClient(thunkApi.dispatch);
    const response = await userClient.deleteUserPreferences();
    customToast({
      content: 'Preferences successfully cleared.',
      type: 'success',
      autoClose: true,
      isAuth: false,
      id: 'clearPreferencesSuccess',
    });
    return response;
  } catch (err) {
    if (instanceOfIErrorDetails(err)) {
      return thunkApi.rejectWithValue(err);
    }
    return thunkApi.rejectWithValue({ statusCode: 500, userMessage: 'We are experiencing technical difficulties.' });
  }
});

export const {
  clearLocationFiltersFromGridDataState,
  receivePersistedState,
  setIncludeExpiredMessages,
  setFilterMessagesByActiveDate,
  setTheme,
  setGridDataState,
  setTrashBinGridState,
  addLocationFilter,
  removeLocationFilter,
  clearLocationFilters,
  setLocationFilter,
  setVisibleColumns,
  resetVisibleColumns,
  setTrashBinVisibleColumns,
  resetTrashBinVisibleColumns,
  adjustColumnSize,
  adjustColumnOrder,
  reset,
} = preferencesSlice.actions;
export default preferencesSlice.reducer;
